import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CountryStateService } from 'src/app/core/services/country-state/country-state.service';
import { LoaderService } from 'src/app/core/services/loader/loader.service';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import {
  SubTenantNode,
  TenantNode,
} from 'src/app/core/interfaces/tenant/tenant.interface';
import { RolesService } from 'src/app/core/services/roles/roles.service';

@Component({
  selector: 'app-role-users',
  templateUrl: './role-users.component.html',
  styleUrls: ['./role-users.component.scss']
})
export class RoleUsersComponent implements OnInit, OnDestroy {
  @Output() selectedDropdown = new EventEmitter<TenantNode[]>();
  private flatNodeMap = new Map<SubTenantNode, TenantNode>();
  private nestedNodeMap = new Map<TenantNode, SubTenantNode>();
  public treeControl: FlatTreeControl<SubTenantNode>;
  private treeFlattener: MatTreeFlattener<TenantNode, SubTenantNode>;
  public dataSource: MatTreeFlatDataSource<TenantNode, SubTenantNode>;
  public checklistSelection = new SelectionModel<SubTenantNode>(true);

  private _unsubscribeAll: Subject<any> = new Subject();

  constructor(
    private _loaderService: LoaderService,
    private _countryStateService: CountryStateService,
    private rolesService: RolesService
  ) { }

  ngOnInit(): void {
    this.getCountryTerritory();

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<SubTenantNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
    // this.initialSubscribers();
  }

  getCountryTerritory() {
    this._loaderService.showLoader();
    this.rolesService.getRolesUser()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(res => {
        let tenantList = res?.data?.map(
          (object: any) => {
            const children = object.users.map(
              (subList: any) => {
                return {
                  item: `${subList.userDetails?.firstName} ${subList.userDetails?.lastName}`,
                  value: subList.id,
                  label: 'sub-tenant',
                };
              }
            );
            return {
              item: object.roleName,
              value: object.roleId,
              label: 'tenant',
              children: children,
            };
          }
        );
        this.dataSource.data = tenantList;
        this._loaderService.hideLoader();
      }, (err) => {
        this._loaderService.hideLoader();
      })
  }

  private getLevel = (node: SubTenantNode) => {
    return node.level;
  };

  private isExpandable = (node: SubTenantNode) => {
    return node.expandable;
  };

  private getChildren = (node: TenantNode): TenantNode[] => {
    return node.children;
  };

  public hasChild = (_: number, _nodeData: SubTenantNode) => {
    return _nodeData.expandable;
  };

  private transformer = (node: TenantNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.item
        ? existingNode
        : new SubTenantNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.value = node.value;
    flatNode.label = node.label;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  private checkAllParentsSelection(node: SubTenantNode): void {
    let parent: SubTenantNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  private checkRootNodeSelection(node: SubTenantNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  private getParentNode(node: SubTenantNode): SubTenantNode | null {
    const currentLevel = this.getLevel(node);
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl?.dataNodes?.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  public descendantsAllSelected(node: SubTenantNode): boolean {
    const descendants = this.treeControl?.getDescendants(node);
    // It will check all the child
    // const descAllSelected = descendants.every((child) =>
    //     this.checklistSelection.isSelected(child)
    // );
    const descAllSelected = this.checklistSelection.isSelected(node);
    return descAllSelected;
  }

  public descendantsPartiallySelected(node: SubTenantNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) =>
      this.checklistSelection.isSelected(child)
    );
    return result && !this.descendantsAllSelected(node);
  }

  public todoItemSelectionToggle(node: SubTenantNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    descendants.every((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  public todoLeafItemSelectionToggle(node: SubTenantNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  public getSelectedItems(): string {
    const selectedItems = this.checklistSelection.selected;
    this.selectedDropdown.emit(selectedItems);
    if (!this.checklistSelection.selected.length)
      return 'Please select users';
    return this.checklistSelection.selected.map((s) => s.item).join(',');
  }

  // Method to check if all items are selected
  public isAllSelected(): boolean {
    const numSelected = this.checklistSelection.selected.length;
    const numRows = this.treeControl.dataNodes?.length;
    return numSelected === numRows;
  }

  // Method to select or deselect all items
  public selectAllItems(event: MatCheckboxChange): void {
    if (event.checked) {
      this.checklistSelection.select(...this.treeControl.dataNodes);
    } else {
      this.checklistSelection.clear();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
